<template>
  <div class="remote-controller" :class="{ embedded: isEmbedded }">
    <!-- Error Display -->
    <div v-if="error" class="error-message">
      <span class="error-icon">⚠️</span>
      <span class="error-text">{{ error }}</span>
      <button @click="error = null" class="error-close">×</button>
    </div>

    <!-- Queue Status Indicator -->
    <div v-if="isQueueBusy" class="queue-status">
      <span class="queue-status-icon">⏳</span>
      <span class="queue-status-text">Queue is being updated...</span>
    </div>

    <PlaybackControls
      v-if="!isEmbedded"
      :is-next-loading="isNextLoading"
      @play="playVideo"
      @pause="pauseVideo"
      @next="playNextVideo"
    />

    <Teleport v-if="isEmbedded && playbackControlsTarget" :to="playbackControlsTarget">
      <PlaybackControls
        state-aware
        :is-playing="isPlaying"
        :is-next-loading="isNextLoading"
        @play="playVideo"
        @pause="pauseVideo"
        @next="playNextVideo"
      />
    </Teleport>

    <button
      v-if="!isEmbedded"
      @click="toggleQueueModal"
      class="queue-button"
    >
      📋 Queue ({{ Math.max(0, queue.length - 1) }})
    </button>

    <!-- Standalone Tab Bar -->
    <div v-if="!isEmbedded" class="tab-bar">
      <button
        :class="['tab', { active: activeTab === 'search' }]"
        @click="activeTab = 'search'"
      >
        Search
      </button>
      <button
        :class="['tab', { active: activeTab === 'mymusic' }]"
        @click="handleMyMusicClick"
      >
        My Music
        <PremiumBadge v-if="!isPremium" size="small" />
        <span class="tab-info-icon" @click.stop="showMyMusicInfo = !showMyMusicInfo">&#8505;&#65039;</span>
      </button>
    </div>

    <!-- Search stays visible throughout the embedded host browser. -->
    <div v-if="isEmbedded || activeTab === 'search'" class="search-box">
      <div class="search-input-wrapper">
        <input
          ref="searchInput"
          v-model="searchQuery"
          @keyup.enter="searchYouTube"
          class="search-input"
          type="search"
          aria-label="Search for a karaoke song"
          placeholder="Search for karaoke songs..."
        />
        <button
          v-if="searchQuery"
          @click="searchQuery = ''; searchResults = []"
          class="clear-button"
          title="Clear search"
        >
          ×
        </button>
      </div>
      <button
        @click="searchYouTube"
        class="search-button"
        :class="{ 'loading': isSearching }"
        :disabled="isSearching"
      >
        {{ isSearching ? 'Searching...' : 'Search' }}
      </button>
    </div>

    <div v-if="isEmbedded" class="tab-bar embedded-tab-bar" role="tablist" aria-label="Song browser">
      <button
        id="host-results-tab"
        role="tab"
        class="tab"
        :class="{ active: activeTab === 'results' }"
        :aria-selected="activeTab === 'results'"
        aria-controls="host-results-panel"
        @click="activeTab = 'results'"
      >
        Search Results
      </button>
      <button
        id="host-queue-tab"
        role="tab"
        class="tab"
        :class="{ active: activeTab === 'queue' }"
        :aria-selected="activeTab === 'queue'"
        aria-controls="host-queue-panel"
        @click="activeTab = 'queue'"
      >
        Queue
        <span class="queue-count-bubble" :aria-label="`${waitingSongCount} songs waiting`">{{ waitingSongCount }}</span>
      </button>
      <div class="tab-with-info" role="presentation">
        <button
          id="host-mymusic-tab"
          role="tab"
          class="tab"
          :class="{ active: activeTab === 'mymusic' }"
          :aria-selected="activeTab === 'mymusic'"
          aria-controls="host-mymusic-panel"
          @click="handleMyMusicClick"
        >
          My Music
          <PremiumBadge v-if="!isPremium" size="small" />
        </button>
        <button
          type="button"
          class="tab-info-button"
          aria-label="About My Music"
          @click="showMyMusicInfo = !showMyMusicInfo"
        >
          &#8505;&#65039;
        </button>
      </div>
    </div>

    <div v-if="showMyMusicInfo" class="my-music-info-tooltip">
      <p>Your personal music library. Favorites and history make it easy to find and replay songs you love.</p>
      <button class="my-music-info-close" @click="showMyMusicInfo = false">&times;</button>
    </div>

    <!-- Search Results -->
    <div
      v-if="isEmbedded || activeTab === 'search'"
      v-show="!isEmbedded || activeTab === 'results'"
      :id="isEmbedded ? 'host-results-panel' : undefined"
      :role="isEmbedded ? 'tabpanel' : undefined"
      :aria-labelledby="isEmbedded ? 'host-results-tab' : undefined"
      class="search-section browser-tab-panel"
    >

      <!-- Search Results -->
      <div v-if="isSearching" class="loading-state search-loading-state">
        Searching...
      </div>
      <div v-else-if="searchResults.length > 0" class="search-results">
        <div class="search-youtube-attribution">
          <a 
            href="https://www.youtube.com/t/terms" 
            target="_blank" 
            rel="noopener noreferrer"
            class="search-youtube-link"
          >
            <span class="search-attribution-text">Powered by YouTube</span>
          </a>
        </div>
        
        <div 
          v-for="video in searchResults" 
          :key="video.id.videoId"
          class="video-item"
        >
          <a 
            :href="`https://www.youtube.com/watch?v=${video.id.videoId}`"
            target="_blank"
            rel="noopener noreferrer"
            class="video-link"
          >
            <img 
              :src="video.snippet.thumbnails.default.url" 
              :alt="video.snippet.title"
              class="video-thumbnail"
            />
            <div class="video-info">
              <h3>{{ video.snippet.title }}</h3>
              <p class="channel-title">{{ video.snippet.channelTitle }}</p>
            </div>
          </a>
          
          <button
            @click="reserveSong(video)"
            class="reserve-button"
            :class="{ 'loading': isReserving[video.id.videoId] }"
            :disabled="isReserving[video.id.videoId]"
            title="Add to queue"
          >
            +
          </button>

          <button
            @click="handleFavorite(video)"
            class="favorite-button"
            :class="{ active: isFavorited(video) }"
            :title="isFavorited(video) ? 'Remove from favorites' : 'Add to favorites'"
          >
            {{ isFavorited(video) ? '♥' : '♡' }}
            <PremiumBadge v-if="!isPremium" size="small" />
          </button>

          <div class="overflow-menu">
            <button 
              @click="toggleOverflowMenu(video)" 
              class="overflow-button"
            >
              ⋮
            </button>
            
            <div v-if="video.showMenu" class="overflow-dropdown">
              <div class="overflow-header">
                <span class="overflow-title">Actions</span>
                <button @click="video.showMenu = false" class="overflow-close">
                  ×
                </button>
              </div>
              <a 
                :href="`https://www.youtube.com/watch?v=${video.id.videoId}`"
                target="_blank"
                rel="noopener noreferrer"
                class="overflow-item"
                @click="video.showMenu = false"
              >
                Watch on YouTube
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="hasSearched && !isSearching && !error" class="no-results">
        No results found
      </div>
      <div v-else-if="!hasSearched && !isSearching && isEmbedded" class="pre-search-prompt">
        Search for a song to see results
      </div>
    </div>

    <!-- My Music Section -->
    <div
      v-if="isEmbedded || activeTab === 'mymusic'"
      v-show="!isEmbedded || activeTab === 'mymusic'"
      :id="isEmbedded ? 'host-mymusic-panel' : undefined"
      :role="isEmbedded ? 'tabpanel' : undefined"
      :aria-labelledby="isEmbedded ? 'host-mymusic-tab' : undefined"
      class="my-music-section browser-tab-panel"
    >
      <div v-if="!isLoggedIn" class="my-music-login-prompt">
        Sign in to access your music library
      </div>
      <div v-else-if="!isPremium" class="my-music-premium-prompt">
        <p>Unlock My Music with Premium</p>
        <button @click="requirePremium('favorites')" class="go-premium-button">
          Go Premium
        </button>
      </div>
      <div v-else>
        <div class="favorites-section">
          <h3 class="section-title">Favorites</h3>
          <div v-if="favoritesLoading" class="loading-state">Loading...</div>
          <div v-else-if="favorites.length === 0" class="empty-state">
            No favorites yet. Tap the heart icon on search results to save songs.
          </div>
          <div v-else class="favorites-list">
            <div
              v-for="fav in favorites"
              :key="fav.id"
              class="video-item"
            >
              <img :src="fav.thumbnail" :alt="fav.title" class="video-thumbnail" />
              <div class="video-info">
                <h3>{{ fav.title }}</h3>
              </div>
              <button
                @click="addFavoriteToQueue(fav)"
                class="reserve-button"
                title="Add to queue"
              >
                +
              </button>
              <button
                @click="removeFavorite(fav)"
                class="favorite-button active"
                title="Remove from favorites"
              >
                ♥
              </button>
            </div>
          </div>
        </div>

        <div class="history-section">
          <h3 class="section-title">Recently Played</h3>
          <div v-if="historyLoading" class="loading-state">Loading...</div>
          <div v-else-if="history.length === 0" class="empty-state">
            No history yet. Songs you add will appear here after they play.
          </div>
          <div v-else class="history-list">
            <div
              v-for="item in history"
              :key="item.id"
              class="video-item"
            >
              <img :src="item.thumbnail" :alt="item.title" class="video-thumbnail" />
              <div class="video-info">
                <h3>{{ item.title }}</h3>
              </div>
              <button
                @click="addHistoryToQueue(item)"
                class="reserve-button"
                title="Add to queue"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Premium Upsell Modal -->
    <PremiumUpsellModal
      v-if="showUpsellModal"
      :feature="upsellFeature"
      @close="showUpsellModal = false"
      @require-sign-in="handleUpsellRequireSignIn"
    />

    <!-- Inline host Queue / standalone Queue modal -->
    <div
      v-if="isEmbedded || showQueueModal"
      v-show="!isEmbedded || activeTab === 'queue'"
      :id="isEmbedded ? 'host-queue-panel' : undefined"
      :role="isEmbedded ? 'tabpanel' : undefined"
      :aria-labelledby="isEmbedded ? 'host-queue-tab' : undefined"
      :class="isEmbedded ? 'queue-tab-panel' : 'queue-overlay'"
      @click="!isEmbedded && closeQueueModal()"
    >
      <div
        :class="isEmbedded ? 'queue-inline-panel' : 'queue-panel'"
        @click.stop
      >
        <div v-if="!isEmbedded" class="queue-header">
          <h2>Queue</h2>
          <button type="button" aria-label="Close queue" @click="closeQueueModal" class="close-button">×</button>
        </div>

        <div class="queue-body">
          <div v-if="queue.length === 0" class="queue-empty">
            No songs in queue yet
          </div>

          <div v-else class="queue-list">
            <div
              v-for="(video, index) in sortedQueue"
              :key="video.id"
              class="queue-item"
              :class="{
                'dragging': draggedIndex === index,
                'drag-over': dragOverIndex === index && index !== 0,
                'not-draggable': index === 0
              }"
              :draggable="index !== 0"
              @dragstart="handleDragStart(index, $event)"
              @dragend="handleDragEnd($event)"
              @dragover.prevent="handleDragOver(index, $event)"
              @dragleave="handleDragLeave()"
              @drop="handleDrop(index, $event)"
            >
              <span v-if="index !== 0" class="drag-handle">☰</span>
              <span class="queue-number">{{ (index + 1) }}</span>
              <img
                :src="video.thumbnail"
                :alt="video.title"
                class="queue-thumbnail"
              />
              <div class="queue-info">
                <h3>{{ video.title }}</h3>
                <p class="channel-title">{{ video.channelTitle }}</p>
                <span v-if="video.addedByName" class="added-by-pill" :style="{ background: video.addedByColor || '#999999' }">
                  {{ video.addedByName }}
                  <SubscriberBadge v-if="video.addedByIsPremium" />
                </span>
              </div>

              <button
                @click="handleFavoriteFromQueue(video)"
                class="favorite-button"
                :class="{ active: isQueueItemFavorited(video) }"
                :title="isQueueItemFavorited(video) ? 'Remove from favorites' : 'Add to favorites'"
              >
                {{ isQueueItemFavorited(video) ? '♥' : '♡' }}
                <PremiumBadge v-if="!isPremium" size="small" />
              </button>

              <div class="overflow-menu">
                <button
                  @click="toggleQueueItemMenu(video)"
                  class="overflow-button"
                >
                  ⋮
                </button>

                <div v-if="video.showMenu" class="overflow-dropdown">
                  <div class="overflow-header">
                    <span class="overflow-title">Actions</span>
                    <button @click="video.showMenu = false" class="overflow-close">
                      ×
                    </button>
                  </div>
                  <a
                    :href="`https://www.youtube.com/watch?v=${video.videoId}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="overflow-item"
                    @click="video.showMenu = false"
                  >
                    Watch on YouTube
                  </a>
                  <button
                    class="overflow-item delete-item"
                    @click="deleteSongFromQueue(video)"
                    :disabled="index === 0 || video.isDeleting"
                  >
                    {{ video.isDeleting ? 'Deleting...' : 'Delete from Queue' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpsCallable } from 'firebase/functions'
import { functions, db } from '../firebase'
import { collection, onSnapshot, addDoc, deleteDoc, doc, query, orderBy, where, getDocs, serverTimestamp, limit, writeBatch } from 'firebase/firestore'
import { reactive, watch } from 'vue'
import { useUserIdentity } from '../utils/userIdentity'
import { useAnalytics } from '../composables/useAnalytics'
import PremiumBadge from './PremiumBadge.vue'
import PremiumUpsellModal from './PremiumUpsellModal.vue'
import SubscriberBadge from './SubscriberBadge.vue'
import PlaybackControls from './PlaybackControls.vue'
import { useSubscription } from '../composables/useSubscription'
import { useAuth } from '../composables/useAuth'
import { deriveHostIsPlaying, getWaitingSongCount } from '../utils/hostPlaybackState.mjs'

export default {
  name: 'RemoteController',
  components: {
    PremiumBadge,
    PremiumUpsellModal,
    SubscriberBadge,
    PlaybackControls
  },
  setup(props) {
    const { isPremium } = useSubscription()
    const { isLoggedIn, user } = useAuth()
    const { userIdentity } = useUserIdentity(props.roomId)
    const { trackEvent } = useAnalytics()

    // Sync display name changes to existing queue items
    watch(() => userIdentity.value.userName, async (newName, oldName) => {
      if (!oldName || !newName || newName === oldName || !user.value?.uid) return
      try {
        const queueRef = collection(db, 'rooms', props.roomId, 'queue')
        const q = query(queueRef, where('addedBy', '==', user.value.uid))
        const snapshot = await getDocs(q)
        if (snapshot.empty) return
        const batch = writeBatch(db)
        snapshot.docs.forEach(d => {
          batch.update(d.ref, { addedByName: newName })
        })
        await batch.commit()
      } catch (err) {
        console.error('Error syncing display name to queue:', err)
      }
    })

    return { isPremium, isLoggedIn, userIdentity, trackEvent }
  },
  props: {
    roomId: {
      type: String,
      required: true
    },
    karaokePlayerRef: {
      type: Object,
      default: null
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    playbackControlsTarget: {
      type: Object,
      default: null
    },
    hostIsCasting: {
      type: Boolean,
      default: false
    },
    hostCastSessionAvailable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      isSearching: false,
      searchResults: [],
      hasSearched: false,
      queue: [],
      isQueueBusy: false,
      error: null,
      isReserving: reactive({}),
      showQueueModal: false,
      draggedIndex: null,
      dragOverIndex: null,
      isReordering: false,
      isNextLoading: false,
      unsubscribe: null,
      unsubscribePlaybackState: null,
      roomIsPaused: null,
      reserveVideoFunction: null,
      pausePlaybackFunction: null,
      resumePlaybackFunction: null,
      skipToNextFunction: null,
      showUpsellModal: false,
      upsellFeature: null,
      showMyMusicInfo: false,
      activeTab: this.isEmbedded ? 'results' : 'search',
      resumeUpsellAfterLogin: false,
      favorites: [],
      favoritesLoading: false,
      unsubscribeFavorites: null,
      history: [],
      historyLoading: false,
      unsubscribeHistory: null
    }
  },
  computed: {
    playbackAvailable() {
      return this.hostIsCasting
        ? this.hostCastSessionAvailable
        : Boolean(this.karaokePlayerRef?.player)
    },
    isPlaying() {
      return deriveHostIsPlaying({
        playbackAvailable: this.playbackAvailable,
        queueLength: this.queue.length,
        roomIsPaused: this.roomIsPaused
      })
    },
    waitingSongCount() {
      return getWaitingSongCount(this.queue.length)
    },
    sortedQueue() {
      return [...this.queue].sort((a, b) => (a.position || 0) - (b.position || 0));
    }
  },
  methods: {
    async searchYouTube() {
      if (!this.searchQuery.trim()) return;

      if (this.isEmbedded) {
        this.activeTab = 'results'
      }
      this.hasSearched = true
      this.isSearching = true;
      this.error = null;
      this.searchResults = [];
      
      try {
        const response = await fetch(`https://www.googleapis.com/youtube/v3/search?part=snippet&maxResults=10&videoEmbeddable=true&q=${encodeURIComponent(this.searchQuery + ' karaoke')}&type=video&key=${process.env.VUE_APP_YOUTUBE_API_KEY}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch videos');
        }
        
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error.message || 'Failed to fetch videos');
        }
        
        this.searchResults = (data.items || []).map(video => ({
          ...video,
          showMenu: false
        }));

        // Log successful search
        this.trackEvent('video_search', {
          room_id: this.roomId,
          query: this.searchQuery,
          results_count: this.searchResults.length
        });
      } catch (error) {
        console.error('Error searching YouTube:', error);
        this.error = 'Failed to search videos. Please try again.';
        this.searchResults = [];

        // Log search error
        this.trackEvent('video_search_error', {
          room_id: this.roomId,
          error_message: error.message || 'Unknown error'
        });
      } finally {
        this.isSearching = false;
      }
    },

    async reserveSong(video, retryCount = 0) {
      this.isReserving[video.id.videoId] = true
      this.isQueueBusy = true
      try {
        const result = await this.reserveVideoFunction({
          roomId: this.roomId,
          video: video,
          userName: this.userIdentity?.userName || 'Anonymous',
          userColor: this.userIdentity?.userColor || '#999999',
          isPremium: this.isPremium
        });

        if (result.data.success) {
          alert(`Song added to queue successfully!`);
          // Log successful song reservation
          this.trackEvent('song_added_to_queue', {
            room_id: this.roomId,
            video_id: video.id.videoId,
            video_title: video.snippet.title
          });
        }
      } catch (error) {
        console.error('Error reserving video:', error);
        
        // Provide specific error message for queue busy
        if (error.message && error.message.includes('Queue is busy')) {
          this.error = 'Queue is busy, please try again in a moment.';
          
          // Retry up to 2 times with exponential backoff
          if (retryCount < 2) {
            const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
            console.log(`Retrying in ${delay}ms (attempt ${retryCount + 1})`);
            
            setTimeout(() => {
              this.reserveSong(video, retryCount + 1);
            }, delay);
            return;
          }
        } else {
          this.error = 'Failed to reserve video. Please try again.';
        }
        
        // Log reservation error
        this.trackEvent('song_reservation_error', {
          room_id: this.roomId,
          video_id: video.id.videoId,
          error_message: error.message || 'Unknown error'
        });
      } finally {
        this.isReserving[video.id.videoId] = false
        this.isQueueBusy = false
      }
    },

    toggleQueueModal() {
      this.showQueueModal = !this.showQueueModal;
      // Log queue modal toggle
      this.trackEvent('queue_modal_toggled', {
        room_id: this.roomId,
        state: this.showQueueModal ? 'opened' : 'closed',
        queue_length: this.queue.length
      });
    },

    closeQueueModal() {
      this.showQueueModal = false;
    },

    toggleOverflowMenu(selectedVideo) {
      // Close all other menus first
      this.searchResults.forEach(video => {
        if (video !== selectedVideo) {
          video.showMenu = false;
        }
      });
      // Toggle the selected menu
      selectedVideo.showMenu = !selectedVideo.showMenu;
    },

    toggleQueueItemMenu(selectedVideo) {
      // Close all other menus first
      this.queue.forEach(video => {
        if (video !== selectedVideo) {
          video.showMenu = false;
        }
      });
      // Toggle the selected menu
      selectedVideo.showMenu = !selectedVideo.showMenu;
    },

    async deleteSongFromQueue(video) {
      // Prevent deletion of currently playing song (first in sorted queue)
      const videoIndex = this.sortedQueue.findIndex(v => v.id === video.id);
      if (videoIndex === 0) {
        this.error = 'Cannot delete the currently playing song. Please use the "Next" button to skip to the next song.';
        video.showMenu = false;
        
        // Log the attempt
        this.trackEvent('song_deletion_blocked_currently_playing', {
          room_id: this.roomId,
          video_id: video.videoId,
          video_title: video.title
        });
        
        return;
      }

      // Confirm deletion
      const confirmed = window.confirm(`Are you sure you want to remove "${video.title}" from the queue?`);
      if (!confirmed) {
        return;
      }

      // Set deleting state
      video.isDeleting = true;
      video.showMenu = false;

      try {
        const removeFromQueueFn = httpsCallable(functions, 'removeFromQueueV2');
        await removeFromQueueFn({
          roomId: this.roomId,
          queueItemId: video.id
        });

        // Log the deletion
        this.trackEvent('song_deleted_from_queue', {
          room_id: this.roomId,
          video_id: video.videoId,
          video_title: video.title
        });

        console.log('Song removed from queue:', video.title);
      } catch (error) {
        console.error('Error deleting song from queue:', error);
        
        // Show user-friendly error message
        if (error.message.includes('Queue is busy')) {
          this.error = 'Queue is busy. Please try again in a moment.';
        } else {
          this.error = 'Failed to delete song from queue. Please try again.';
        }

        // Log the error
        this.trackEvent('song_deletion_error', {
          room_id: this.roomId,
          video_id: video.videoId,
          error_message: error.message || 'Unknown error'
        });
      } finally {
        // Clear deleting state
        video.isDeleting = false;
      }
    },

    handleDragStart(index, event) {
      console.log('Drag start:', { index, sortedQueue: this.sortedQueue });
      
      // Prevent dragging the currently playing song (first item in sorted queue)
      if (index === 0) {
        event.preventDefault();
        return;
      }
      
      const video = this.sortedQueue[index];
      console.log('Video at index:', video);
      
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', event.target);
      
      console.log('Drag started for index:', index);
      
      // Add a slight delay to apply dragging class (for visual feedback)
      setTimeout(() => {
        if (event.target) {
          event.target.classList.add('dragging');
        }
      }, 0);
    },

    handleDragEnd(event) {
      this.draggedIndex = null;
      this.dragOverIndex = null;
      
      // Remove dragging class
      if (event.target) {
        event.target.classList.remove('dragging');
      }
    },

    handleDragOver(index, event) {
      event.preventDefault();
      
      // Can't drop on the currently playing song (first item in sorted queue)
      if (index === 0) {
        event.dataTransfer.dropEffect = 'none';
        this.dragOverIndex = null;
        return;
      }
      
      event.dataTransfer.dropEffect = 'move';
      this.dragOverIndex = index;
    },

    handleDragLeave() {
      this.dragOverIndex = null;
    },

    async handleDrop(dropIndex, event) {
      event.preventDefault();
      this.dragOverIndex = null;
      
      console.log('Drop event:', { dropIndex, draggedIndex: this.draggedIndex });
      
      if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
        console.log('Drop cancelled: same position or no drag');
        return;
      }
      
      // Can't drop on the currently playing song (first item in sorted queue)
      if (dropIndex === 0) {
        console.log('Drop cancelled: target is currently playing (index 0)');
        return;
      }
      
      // Prevent dropping if already reordering
      if (this.isReordering) {
        this.error = 'Queue is being reordered. Please wait.';
        return;
      }
      
      this.isReordering = true;
      
      try {
        console.log('Starting reorder operation...');
        
        // Separate the currently playing song (position 1) from the rest
        const currentlyPlaying = this.sortedQueue[0]; // Position 1 - never changes
        const queueableItems = this.sortedQueue.slice(1); // Position 2+ - can be reordered
        
        console.log('Currently playing (locked):', { id: currentlyPlaying.id, position: currentlyPlaying.position, title: currentlyPlaying.title });
        console.log('Queueable items:', queueableItems.map(v => ({ id: v.id, position: v.position, title: v.title })));
        
        // Adjust indices to work with queueableItems array (subtract 1 since we removed first item)
        const adjustedDraggedIndex = this.draggedIndex - 1;
        const adjustedDropIndex = dropIndex - 1;
        
        // Remove the dragged item from queueable items
        const [draggedItem] = queueableItems.splice(adjustedDraggedIndex, 1);
        console.log('Dragged item:', { id: draggedItem.id, title: draggedItem.title });
        
        // Insert at new position in queueable items
        queueableItems.splice(adjustedDropIndex, 0, draggedItem);
        console.log('Reordered queueable items:', queueableItems.map(v => ({ id: v.id, position: v.position, title: v.title })));
        
        // Calculate new positions for queueable items only (starting from position 2)
        const positionUpdates = queueableItems.map((item, index) => ({
          id: item.id,
          position: index + 2  // Start from position 2
        }));
        
        console.log('Position updates (excluding position 1):', positionUpdates);
        
        // Call the Cloud Function to update positions
        const batchUpdateFn = httpsCallable(functions, 'batchUpdateQueuePositionsV2');
        const result = await batchUpdateFn({
          roomId: this.roomId,
          positionUpdates: positionUpdates,
          userName: this.userIdentity?.userName,
          userColor: this.userIdentity?.userColor,
          isPremium: this.isPremium
        });
        
        console.log('Cloud function result:', result);
        
        // Log the reorder
        this.trackEvent('queue_reordered', {
          room_id: this.roomId,
          from_position: this.draggedIndex + 1,
          to_position: dropIndex + 1
        });
        
        console.log('Queue reordered successfully');
      } catch (error) {
        console.error('Error reordering queue:', error);
        console.error('Error details:', error.message, error.code, error.details);
        
        // Show user-friendly error message
        if (error.message && error.message.includes('Queue is busy')) {
          this.error = 'Queue is busy. Please try again in a moment.';
        } else {
          this.error = `Failed to reorder queue: ${error.message || 'Unknown error'}`;
        }
        
        // Log the error
        this.trackEvent('queue_reorder_error', {
          room_id: this.roomId,
          error_message: error.message || 'Unknown error'
        });
      } finally {
        this.isReordering = false;
        this.draggedIndex = null;
      }
    },

    async playNextVideo() {
      if (this.isNextLoading) return;
      
      this.isNextLoading = true;
      this.isQueueBusy = true;
      try {
        console.log('Skipping to next song via Cloud Function');

        if (this.queue.length <= 1) {
            console.warn('Queue has 1 or fewer items, cannot play next.');
            return;
        }

        // Call Cloud Function to skip to next
        const result = await this.skipToNextFunction({
          roomId: this.roomId,
          userName: this.userIdentity?.userName || 'Anonymous',
          userColor: this.userIdentity?.userColor || '#999999',
          isPremium: this.isPremium
        });

        if (result.data.success) {
          console.log('Successfully skipped to next song:', result.data.skippedSong);
          
          // Log next song played
          this.trackEvent('next_song_played', {
            room_id: this.roomId,
            video_id: result.data.skippedSong?.videoId,
            video_title: result.data.skippedSong?.title,
            queue_length: this.queue.length
          });
        }
      } catch (error) {
        console.error('Error playing next video:', error);
        this.error = error.message || 'Failed to skip to next song';
        this.trackEvent('next_song_error', {
          room_id: this.roomId,
          error_message: error.message || 'Unknown error'
        });
      } finally {
        this.isNextLoading = false;
        this.isQueueBusy = false;
      }
    },

    async playVideo() {
      console.log('Playing video');
      try {
        if (this.isEmbedded && this.hostIsCasting) {
          const session = this.getCurrentCastSession()
          if (!session) {
            this.error = 'Playback device is unavailable.'
            return
          }

          try {
            await session.sendMessage('urn:x-cast:com.google.cast.media', { type: 'PLAY' })
            console.log('Play command sent successfully to Cast')
            this.trackEvent('play_command_sent', {
              room_id: this.roomId
            })
          } catch (error) {
            console.error('Error sending play command:', error)
            this.trackEvent('play_command_error', {
              room_id: this.roomId,
              error_message: error.message || 'Unknown error'
            })
          }
          return
        }

        if (this.isEmbedded && !this.karaokePlayerRef?.player) {
          this.error = 'Playback device is unavailable.'
          return
        }

        // If we have a karaoke player reference (desktop mode), use it directly
        if (this.karaokePlayerRef) {
          console.log('Using KaraokePlayer ref for play');
          this.karaokePlayerRef.playVideo();
          this.trackEvent('play_command_sent_desktop', {
            room_id: this.roomId
          });
          return;
        }

        // Otherwise, try to use Cast session (mobile/standalone mode)
        const session = this.getCurrentCastSession()
        
        // If we have a cast session, use it
        if (session) {
          session.sendMessage('urn:x-cast:com.google.cast.media', {
            type: 'PLAY'
          }).then(() => {
            console.log('Play command sent successfully to Cast');
            this.trackEvent('play_command_sent', {
              room_id: this.roomId
            });
          }).catch(error => {
            console.error('Error sending play command:', error);
            this.trackEvent('play_command_error', {
              room_id: this.roomId,
              error_message: error.message || 'Unknown error'
            });
          });
          return;
        }

        // If no cast session and no karaoke player ref, use Cloud Function
        console.log('No cast session or player ref, calling Cloud Function');
        try {
          const result = await this.resumePlaybackFunction({
            roomId: this.roomId,
            userName: this.userIdentity?.userName || 'Anonymous',
            userColor: this.userIdentity?.userColor || '#999999',
            isPremium: this.isPremium
          });
          
          if (result.data.success) {
            console.log('Play state updated via Cloud Function');
            this.trackEvent('play_command_sent_function', {
              room_id: this.roomId
            });
          }
        } catch (updateError) {
          console.error('Error updating play state:', updateError);
          this.error = 'Failed to play. Please try again.';
        }
      } catch (error) {
        console.error('Error in playVideo:', error);
      }
    },

    async pauseVideo() {
      console.log('Pausing video - button clicked');
      
      try {
        if (this.isEmbedded && this.hostIsCasting) {
          const session = this.getCurrentCastSession()
          if (!session) {
            this.error = 'Playback device is unavailable.'
            return
          }

          try {
            await session.sendMessage('urn:x-cast:com.google.cast.media', { type: 'PAUSE' })
            console.log('Pause command sent successfully to Cast')
            this.trackEvent('pause_command_sent', {
              room_id: this.roomId
            })
          } catch (error) {
            console.error('Error sending pause command:', error)
            this.error = `Failed to pause: ${error.message || 'Unknown error'}`
            this.trackEvent('pause_command_error', {
              room_id: this.roomId,
              error_message: error.message || 'Unknown error'
            })
          }
          return
        }

        if (this.isEmbedded && !this.karaokePlayerRef?.player) {
          this.error = 'Playback device is unavailable.'
          return
        }

        console.log('karaokePlayerRef:', this.karaokePlayerRef);

        // If we have a karaoke player reference (desktop mode), use it directly
        if (this.karaokePlayerRef) {
          console.log('Using KaraokePlayer ref for pause');
          this.karaokePlayerRef.pauseVideo();
          this.trackEvent('pause_command_sent_desktop', {
            room_id: this.roomId
          });
          return;
        }

        // Otherwise, try to use Cast session (mobile/standalone mode)
        const session = this.getCurrentCastSession()
        
        console.log('Current session:', session);
        
        // If we have a cast session, use it
        if (session) {
          const message = { type: 'PAUSE' };
          console.log('Sending PAUSE message to Cast:', message);
          
          session.sendMessage('urn:x-cast:com.google.cast.media', message).then(() => {
            console.log('Pause command sent successfully to Cast');
            this.trackEvent('pause_command_sent', {
              room_id: this.roomId
            });
          }).catch(error => {
            console.error('Error sending pause command:', error);
            this.error = `Failed to pause: ${error.message || 'Unknown error'}`;
            this.trackEvent('pause_command_error', {
              room_id: this.roomId,
              error_message: error.message || 'Unknown error'
            });
          });
          return;
        }
        
        // If no cast session and no karaoke player ref, use Cloud Function
        console.log('No cast session or player ref, calling Cloud Function');
        try {
          const result = await this.pausePlaybackFunction({
            roomId: this.roomId,
            userName: this.userIdentity?.userName || 'Anonymous',
            userColor: this.userIdentity?.userColor || '#999999',
            isPremium: this.isPremium
          });
          
          if (result.data.success) {
            console.log('Pause state updated via Cloud Function');
            this.trackEvent('pause_command_sent_function', {
              room_id: this.roomId
            });
          }
        } catch (updateError) {
          console.error('Error updating pause state:', updateError);
          this.error = 'Failed to pause. Please try again.';
        }
      } catch (error) {
        console.error('Error in pauseVideo:', error);
        this.error = `Error pausing video: ${error.message}`;
      }
    },

    requirePremium(feature) {
      if (!this.isPremium) {
        this.upsellFeature = feature
        this.showUpsellModal = true
        return false
      }
      return true
    },
    handleMyMusicClick() {
      this.activeTab = 'mymusic'
      if (!this.isLoggedIn) {
        this.upsellFeature = 'favorites'
        this.showUpsellModal = true
      }
    },
    handleUpsellRequireSignIn() {
      this.resumeUpsellAfterLogin = true
      this.showUpsellModal = false
      window.dispatchEvent(new Event('auth:open-sign-in'))
    },

    isFavorited(video) {
      const videoId = video.id?.videoId || video.videoId
      return this.favorites.some(f => f.videoId === videoId)
    },

    isQueueItemFavorited(video) {
      return this.favorites.some(f => f.videoId === video.videoId)
    },

    handleFavoriteFromQueue(video) {
      if (!this.requirePremium('favorites')) return
      const existing = this.favorites.find(f => f.videoId === video.videoId)
      if (existing) {
        this.removeFavorite(existing)
      } else {
        this.addToFavorites({
          videoId: video.videoId,
          title: video.title,
          thumbnail: video.thumbnail
        })
      }
    },

    handleFavorite(video) {
      if (!this.requirePremium('favorites')) return
      const videoId = video.id?.videoId || video.videoId
      const existing = this.favorites.find(f => f.videoId === videoId)
      if (existing) {
        this.removeFavorite(existing)
      } else {
        this.addToFavorites(video)
      }
    },

    setupFavoritesListener() {
      const { user } = useAuth()
      if (!user.value?.uid) return

      const favRef = collection(db, 'users', user.value.uid, 'favorites')
      const favQuery = query(favRef, orderBy('addedAt', 'desc'))

      this.favoritesLoading = true
      this.unsubscribeFavorites = onSnapshot(favQuery, (snapshot) => {
        this.favorites = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        this.favoritesLoading = false
      }, (error) => {
        console.error('Error listening to favorites:', error)
        this.favoritesLoading = false
      })
    },

    async addToFavorites(video) {
      const { user } = useAuth()
      if (!user.value?.uid) return

      const videoId = video.id?.videoId || video.videoId
      const title = video.snippet?.title || video.title
      const thumbnail = video.snippet?.thumbnails?.default?.url || video.thumbnail

      const favRef = collection(db, 'users', user.value.uid, 'favorites')
      const existing = await getDocs(query(favRef, where('videoId', '==', videoId)))
      if (!existing.empty) {
        this.error = 'Already in favorites'
        setTimeout(() => { this.error = null }, 2000)
        return
      }

      await addDoc(favRef, {
        videoId,
        title,
        thumbnail,
        addedAt: serverTimestamp()
      })

      this.trackEvent('favorite_added', { video_id: videoId })
    },

    async removeFavorite(fav) {
      const { user } = useAuth()
      if (!user.value?.uid) return

      await deleteDoc(doc(db, 'users', user.value.uid, 'favorites', fav.id))
      this.trackEvent('favorite_removed', { video_id: fav.videoId })
    },

    addFavoriteToQueue(fav) {
      const video = {
        id: { videoId: fav.videoId },
        snippet: {
          title: fav.title,
          channelTitle: '',
          thumbnails: { default: { url: fav.thumbnail } }
        }
      }
      this.reserveSong(video)
    },

    setupHistoryListener() {
      const { user } = useAuth()
      if (!user.value?.uid) return

      const histRef = collection(db, 'users', user.value.uid, 'history')
      const histQuery = query(histRef, orderBy('playedAt', 'desc'), limit(50))

      this.historyLoading = true
      this.unsubscribeHistory = onSnapshot(histQuery, (snapshot) => {
        this.history = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        this.historyLoading = false
      }, (error) => {
        console.error('Error listening to history:', error)
        this.historyLoading = false
      })
    },

    addHistoryToQueue(item) {
      const video = {
        id: { videoId: item.videoId },
        snippet: {
          title: item.title,
          channelTitle: '',
          thumbnails: { default: { url: item.thumbnail } }
        }
      }
      this.reserveSong(video)
    },

    setupQueueListener() {
      const queueRef = collection(db, 'rooms', this.roomId, 'queue');
      this.unsubscribe = onSnapshot(queueRef, snapshot => {
        this.queue = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }, error => {
        console.error("Error listening to Firestore: ", error);
      });
    },

    getCurrentCastSession() {
      try {
        return cast.framework.CastContext.getInstance().getCurrentSession()
      } catch (error) {
        console.log('Cast framework not available:', error)
        return null
      }
    },

    setupPlaybackStateListener() {
      this.unsubscribePlaybackState?.()
      const roomRef = doc(db, 'rooms', this.roomId)
      this.unsubscribePlaybackState = onSnapshot(roomRef, snapshot => {
        const value = snapshot.data()?.isPaused
        this.roomIsPaused = typeof value === 'boolean' ? value : null
      }, error => {
        console.error('Error listening to playback state:', error)
        this.roomIsPaused = null
      })
    }
  },

  watch: {
    activeTab(newTab) {
      if (newTab === 'mymusic' && this.isPremium && this.isLoggedIn && !this.unsubscribeFavorites) {
        this.setupFavoritesListener()
      }
      if (newTab === 'mymusic' && this.isPremium && this.isLoggedIn && !this.unsubscribeHistory) {
        this.setupHistoryListener()
      }
    },
    isPremium(newVal) {
      if (newVal && this.activeTab === 'mymusic' && this.isLoggedIn && !this.unsubscribeFavorites) {
        this.setupFavoritesListener()
      }
      if (newVal && this.activeTab === 'mymusic' && this.isLoggedIn && !this.unsubscribeHistory) {
        this.setupHistoryListener()
      }
    },
    isLoggedIn(newVal) {
      if (newVal && this.resumeUpsellAfterLogin) {
        this.showUpsellModal = true
        this.resumeUpsellAfterLogin = false
      }
    }
  },

  mounted() {
    console.log('RemoteController mounted for room:', this.roomId);
    this.reserveVideoFunction = httpsCallable(functions, 'reserveVideoV2');
    this.pausePlaybackFunction = httpsCallable(functions, 'pausePlaybackV2');
    this.resumePlaybackFunction = httpsCallable(functions, 'resumePlaybackV2');
    this.skipToNextFunction = httpsCallable(functions, 'skipToNextV2');
    this.setupQueueListener();
    this.setupPlaybackStateListener();

    if (this.isPremium && this.isLoggedIn) {
      this.setupFavoritesListener()
      this.setupHistoryListener()
    }
    
    // Log screen view
    this.trackEvent('remote_controller_viewed', {
      room_id: this.roomId
    });

    this.$nextTick(() => {
      const desktop = window.matchMedia?.('(min-width: 769px)').matches
      const activeElement = document.activeElement
      const focusIsUnclaimed = !activeElement || activeElement === document.body
      if (this.isEmbedded && desktop && focusIsUnclaimed) {
        this.$refs.searchInput?.focus()
      }
    })
  },

  beforeUnmount() {
    this.unsubscribePlaybackState?.()
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.unsubscribeFavorites) {
      this.unsubscribeFavorites()
    }
    if (this.unsubscribeHistory) {
      this.unsubscribeHistory()
    }
  }
}
</script>

<style scoped>
.remote-controller {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.remote-controller.embedded {
  gap: 0.85rem;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.remote-controller.embedded .search-input,
.remote-controller.embedded .search-button {
  min-height: 52px;
}

.remote-controller.embedded .search-input {
  padding-left: 1rem;
  border: 1px solid rgba(255, 165, 0, 0.55);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.06);
}

.remote-controller.embedded .search-button {
  padding-inline: 1rem;
  background: #ffa500;
  color: #1a0c00;
  font-weight: 700;
}

/* Error Message Styles */
.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 68, 68, 0.3);
  animation: slideIn 0.3s ease-out;
}

.error-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.error-text {
  flex: 1;
  line-height: 1.4;
}

.error-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.error-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Queue Status Indicator */
.queue-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #ffa500 0%, #ff8c00 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 165, 0, 0.3);
  animation: pulse 2s infinite;
}

.queue-status-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.queue-status-text {
  flex: 1;
  line-height: 1.4;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.remote-control {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.remote-button {
  width: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem 0.5rem;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 20px;
  transition: all 0.2s;
  cursor: pointer;
}

.remote-button:hover {
  background: rgba(255, 165, 0, 0.2);
  transform: translateY(-1px);
}

.remote-button:active {
  transform: translateY(0);
}

.button-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
}

.tab-bar {
  display: flex;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
}

.tab {
  flex: 1;
  padding: 0.5rem;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.tab.active {
  background: rgba(255, 165, 0, 0.2);
  color: #ffa500;
}

.tab:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
}

.tab:focus-visible,
.tab-info-button:focus-visible {
  outline: 2px solid #ffa500;
  outline-offset: -2px;
}

.embedded-tab-bar {
  flex-shrink: 0;
  overflow: visible;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px 8px 0 0;
}

.embedded-tab-bar .tab {
  min-width: 0;
  min-height: 44px;
  border-bottom: 2px solid transparent;
  border-radius: 8px 8px 0 0;
}

.embedded-tab-bar .tab.active {
  border-bottom-color: #ffa500;
}

.tab-with-info {
  display: flex;
  flex: 1;
  min-width: 0;
}

.tab-with-info .tab {
  flex: 1;
}

.tab-info-button {
  flex: 0 0 36px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
}

.tab-info-button:hover {
  color: #ffa500;
  background: rgba(255, 165, 0, 0.1);
}

.queue-count-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ffa500;
  color: #1a0c00;
  font-size: 0.7rem;
  font-weight: 800;
  line-height: 1;
  box-sizing: border-box;
}

.tab-info-icon {
  font-size: 0.75rem;
  opacity: 0.5;
  transition: opacity 0.2s;
  margin-left: 2px;
}

.tab-info-icon:hover {
  opacity: 1;
}

.my-music-info-tooltip {
  position: relative;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  border-radius: 8px;
  padding: 0.75rem 2rem 0.75rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: tooltipFadeIn 0.2s ease-out;
}

.my-music-info-tooltip p {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
}

.my-music-info-close {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
}

.my-music-info-close:hover {
  color: white;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: calc(100vh - 160px);
  min-height: 0;
}

.remote-controller.embedded .browser-tab-panel,
.remote-controller.embedded .queue-tab-panel {
  flex: 1;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.remote-controller.embedded .search-results {
  max-height: none;
  overflow: visible;
}

.pre-search-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 1rem;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
}

.search-box {
  display: flex;
  gap: 0.5rem;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.clear-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  transition: all 0.2s;
}

.clear-button:hover {
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-weight: 400;
  transition: background-color 0.2s;
}

.search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.search-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.15);
}

.search-button,
.queue-button {
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.search-button:hover,
.queue-button:hover {
  background: rgba(255, 165, 0, 0.2);
  transform: translateY(-1px);
}

.search-button:active,
.queue-button:active {
  transform: translateY(0);
}

.search-button.loading,
.queue-button.loading,
.reserve-button.loading {
  pointer-events: none;
  opacity: 0.8;
}

.search-button.loading::after,
.queue-button.loading::after,
.reserve-button.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 25%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 100%
  );
  animation: shimmer 1s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.search-results {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: calc(100vh - 210px);
  -webkit-overflow-scrolling: touch;
}

.search-youtube-attribution {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 8px;
}

.search-youtube-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.search-attribution-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  transition: color 0.2s;
}

.search-youtube-link:hover .search-attribution-text {
  color: rgba(255, 255, 255, 0.9);
}

.my-music-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  max-height: calc(100vh - 250px);
}

.my-music-login-prompt,
.my-music-premium-prompt {
  text-align: center;
  padding: 2rem 1rem;
  color: rgba(255, 255, 255, 0.7);
}

.go-premium-button {
  margin-top: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #ffa500, #ff8c00);
  border: none;
  border-radius: 8px;
  color: black;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;
}

.go-premium-button:hover {
  transform: translateY(-1px);
}

.section-title {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0 0 0.5rem;
}

.empty-state,
.loading-state {
  text-align: center;
  padding: 1.5rem;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
}

.video-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 0;
}

.video-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  text-decoration: none;
  color: white;
  min-width: 0;
  overflow: hidden;
}

.video-thumbnail {
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
}

.video-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.video-info h3 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  max-height: 2.4em;
}

.video-info p {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reserve-button {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #ffa500;
  color: white;
  font-size: 1.125rem;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  margin-left: 0.25rem;
  cursor: pointer;
}

.reserve-button:hover {
  background: #ff9500;
  transform: translateY(-1px);
}

.reserve-button:active {
  transform: translateY(0);
}

.favorite-button {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
  position: relative;
}

.favorite-button:hover {
  color: #ffa500;
  background: rgba(255, 165, 0, 0.1);
}

.favorite-button.active {
  color: #ff4757;
}

.overflow-menu {
  position: relative;
  margin-left: 0.25rem;
}

.overflow-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s;
}

.overflow-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.overflow-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}

.overflow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.overflow-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
}

.overflow-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.overflow-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

.overflow-item {
  display: block;
  padding: 0.5rem;
  color: white;
  text-decoration: none;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.overflow-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.overflow-item.delete-item {
  color: #ff3b30;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.25rem;
  padding-top: 0.75rem;
}

.overflow-item.delete-item:hover:not(:disabled) {
  background: rgba(255, 59, 48, 0.1);
}

.overflow-item.delete-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.5);
}

.overflow-item.delete-item:disabled:hover {
  background: none;
}

.channel-title {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #8e8e93;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.added-by-pill {
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0.25rem 0.625rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  border-radius: 12px;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.queue-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.95);
  border-left: none;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
}

.queue-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.queue-tab-panel {
  position: static;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  background: transparent;
}

.queue-inline-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
}

.queue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.queue-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: white;
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.queue-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.queue-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.queue-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

.queue-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 0;
  cursor: grab;
  transition: all 0.2s;
  position: relative;
  color: white;
}

.queue-item[draggable="true"]:active {
  cursor: grabbing;
}

.queue-item.not-draggable {
  cursor: default;
  opacity: 0.9;
}

.queue-item.dragging {
  opacity: 0.5;
  cursor: grabbing;
  transform: scale(0.95);
}

.queue-item.drag-over {
  border: 2px dashed #ffa500;
  background: rgba(255, 165, 0, 0.1);
}

.queue-item.not-draggable.drag-over {
  border: none;
  background: rgba(255, 255, 255, 0.1);
}

.drag-handle {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 1rem;
  cursor: grab;
  padding: 0.25rem;
  transition: color 0.2s;
  user-select: none;
}

.queue-item:hover .drag-handle {
  color: rgba(255, 255, 255, 0.7);
}

.queue-item.dragging .drag-handle {
  cursor: grabbing;
}

.queue-number {
  flex-shrink: 0;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  min-width: 24px;
  text-align: center;
}

.queue-thumbnail {
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
}

.queue-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.queue-info h3 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  max-height: 2.4em;
}

.queue-info p {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-results, .loading {
  text-align: center;
  padding: 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

@media (max-width: 768px) {
  .remote-controller:not(.embedded) .queue-overlay {
    top: 60px;
  }
}

@media (max-width: 480px) {
  .embedded-tab-bar .tab {
    padding: 0.45rem 0.3rem;
    font-size: 0.75rem;
  }

  .tab-info-button {
    flex-basis: 30px;
    padding: 0;
  }

  .queue-count-bubble {
    min-width: 18px;
    height: 18px;
    padding-inline: 4px;
    font-size: 0.65rem;
  }

  .remote-controller.embedded .search-input,
  .remote-controller.embedded .search-button {
    min-height: 46px;
  }

  .video-item, .queue-item {
    padding: 0.375rem;
  }
}
</style>
